import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Copy, Loader2, Send } from "lucide-react";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { useAuth } from "../../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function fascicoloLabel(f) {
  const cognome = (f.cognome || f.anagrafica?.cognome || "").trim();
  const nome = (f.nome || f.anagrafica?.nome || "").trim();
  const full = `${cognome} ${nome}`.trim();
  return full || (f.nome_completo || "").trim() || "Ospite";
}

export default function InviaModuloDialog({ open, onOpenChange, modulo, token }) {
  const { activeStruttura } = useAuth();
  const [fascicoli, setFascicoli] = useState([]);
  const [fascicoloId, setFascicoloId] = useState("");
  const [destinatarioTipo, setDestinatarioTipo] = useState("familiare_principale");
  const [email, setEmail] = useState("");
  const [firmaDigitale, setFirmaDigitale] = useState(true);
  const [uploadPdf, setUploadPdf] = useState(true);
  const [messaggio, setMessaggio] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loadingFascicoli, setLoadingFascicoli] = useState(false);

  useEffect(() => {
    if (!open || !token) return;
    setResult(null);
    setCopied(false);
    setFascicoloId("");
    setLoadingFascicoli(true);
    axios.get(`${API_URL}/api/fascicoli-assistenziali`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        const raw = Array.isArray(r.data) ? r.data : r.data?.fascicoli || r.data?.items || [];
        // Solo fascicoli attivi: l'API invia esclude archiviata=true
        const attivi = raw
          .filter((f) => !f.archiviata && (f.id || f._id))
          .sort((a, b) => fascicoloLabel(a).localeCompare(fascicoloLabel(b), "it"));
        setFascicoli(attivi);
      })
      .catch((err) => {
        setFascicoli([]);
        toast.error(err.response?.data?.detail || "Errore caricamento ospiti");
      })
      .finally(() => setLoadingFascicoli(false));
    if (modulo) {
      setMessaggio(`La preghiamo di completare il documento «${modulo.titolo}». Grazie.`);
    }
  }, [open, token, modulo, activeStruttura?.id]);

  const invia = async () => {
    if (!fascicoloId) { toast.error("Seleziona un ospite"); return; }
    if (destinatarioTipo === "altro" && !email.trim()) { toast.error("Inserisci email"); return; }
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/moduli-firma/${modulo.id}/invia`,
        {
          fascicolo_id: fascicoloId,
          destinatario_tipo: destinatarioTipo,
          email: destinatarioTipo === "altro" ? email.trim() : undefined,
          modalita_firma_digitale: firmaDigitale,
          modalita_upload_pdf: uploadPdf,
          messaggio,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Inviato a ${data.email}`);
      setResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio");
    } finally {
      setSending(false);
    }
  };

  const copyLink = () => {
    if (!result?.portal_link) return;
    navigator.clipboard.writeText(result.portal_link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (!modulo) return null;

  if (result) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Modulo inviato</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <Send className="h-5 w-5 shrink-0 text-emerald-600" />
              <p className="text-sm text-slate-700">
                Email inviata a <strong>{result.email}</strong> con il link al documento «{modulo.titolo}».
              </p>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500">Link sicuro al portale</Label>
              <div className="mt-1 flex items-center gap-2">
                <Input readOnly value={result.portal_link || ""} className="text-xs" />
                <Button type="button" variant="outline" size="icon" className="h-10 w-10 shrink-0" onClick={copyLink} title="Copia link">
                  {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <p className="mt-1 text-[11px] text-slate-400">
                Utile da inviare manualmente (WhatsApp, SMS...) se l'email non arriva. Valido 14 giorni.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button className="bg-[#00A86B] hover:bg-[#00915d]" onClick={() => onOpenChange(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Invia al familiare — {modulo.titolo}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-6 md:grid-cols-5">
          <div className="space-y-4 md:col-span-3">
            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500">Seleziona destinatario</Label>
              <Select value={fascicoloId} onValueChange={setFascicoloId} disabled={loadingFascicoli}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={loadingFascicoli ? "Caricamento ospiti..." : "Ospite / fascicolo"} />
                </SelectTrigger>
                <SelectContent>
                  {fascicoli.length === 0 && !loadingFascicoli ? (
                    <div className="px-3 py-2 text-sm text-slate-500">Nessun ospite attivo trovato</div>
                  ) : (
                    fascicoli.map((f) => {
                      const fid = String(f.id || f._id);
                      return (
                        <SelectItem key={fid} value={fid}>
                          {fascicoloLabel(f)}
                          {f.camera && f.camera !== "N/A" ? ` — ${f.camera}` : ""}
                        </SelectItem>
                      );
                    })
                  )}
                </SelectContent>
              </Select>
            </div>
            <RadioGroup value={destinatarioTipo} onValueChange={setDestinatarioTipo} className="space-y-2">
              <div className="flex items-center gap-2"><RadioGroupItem value="familiare_principale" id="fam" /><Label htmlFor="fam">Familiare principale</Label></div>
              <div className="flex items-center gap-2"><RadioGroupItem value="tutore" id="tut" /><Label htmlFor="tut">Tutore</Label></div>
              <div className="flex items-center gap-2"><RadioGroupItem value="altro" id="alt" /><Label htmlFor="alt">Altro indirizzo email</Label></div>
            </RadioGroup>
            {destinatarioTipo === "altro" && (
              <InputEmail value={email} onChange={setEmail} />
            )}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase text-slate-500">Modalità di completamento</Label>
              <label className="flex items-center gap-2"><Checkbox checked={firmaDigitale} onCheckedChange={setFirmaDigitale} /><span className="text-sm">Firma digitale con Affidya</span></label>
              <label className="flex items-center gap-2"><Checkbox checked={uploadPdf} onCheckedChange={setUploadPdf} /><span className="text-sm">Caricamento PDF firmato</span></label>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500">Messaggio (modificabile)</Label>
              <Textarea value={messaggio} onChange={(e) => setMessaggio(e.target.value)} rows={4} className="mt-1" />
            </div>
          </div>
          <aside className="rounded-xl border bg-slate-50 p-4 text-sm text-slate-600 md:col-span-2">
            <p className="font-medium text-slate-800">Cosa riceverà il familiare</p>
            <ul className="mt-2 space-y-1 text-xs">
              <li>• Email con link sicuro</li>
              <li>• Accesso al portale documenti</li>
              <li>• Link valido 14 giorni</li>
              <li>• Firma digitale o upload PDF</li>
            </ul>
          </aside>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button className="bg-[#00A86B] hover:bg-[#00915d]" onClick={invia} disabled={sending}>
            {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            Invia
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InputEmail({ value, onChange }) {
  return (
    <input
      type="email"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="email@esempio.it"
      className="flex h-10 w-full rounded-md border border-slate-200 px-3 text-sm"
    />
  );
}
